import { Helper } from "../common/helper";
import { TimesheetRightTableColumn } from "./timesheet-models";
import { TableHelper } from "../common/table-helper";
import { TimesheetCommentsManager } from "./timesheet-comments-manager";
import { Icon, IconHelper } from "../common/icon-helper";
import { TimesheetRightTableDataManager } from "./timesheet-right-table-data-manager";

export class TimesheetRightTableFormatter
{
    private readonly columns: TimesheetRightTableColumn[];
    private readonly borderRows: number[];
    private readonly dataManager: TimesheetRightTableDataManager;

    constructor(columns: TimesheetRightTableColumn[], borderRows: number[], dataManager: TimesheetRightTableDataManager)
    {
        this.columns = columns;
        this.borderRows = borderRows;
        this.dataManager = dataManager;
    }

    getColumnHeader(columnNumber: number): string
    {
        const column = this.columns[columnNumber];
        return TableHelper.getDateCellHtml(column.Date, column.Color);
    }

    getRowHeader(rowNumber: number, commentsManager: TimesheetCommentsManager): string
    {
        const rowData = this.dataManager.getData().getRow(rowNumber);
        const comment = commentsManager.getComment(rowNumber, null);
        return Helper.isMobileScreen() ? rowData.FullName :
            comment ? `${rowData.ShortTitle} (${comment})` : rowData.ShortTitle;
    }

    formatCell(rowNumber: number, columnNumber: number, cell, value)
    {
        const borderColor = this.columns[columnNumber].BorderColor;
        if (borderColor)
        {
            cell.css("border-bottom", "1px solid " + borderColor);
            if (columnNumber === 0 || this.columns[columnNumber - 1].BorderColor !== borderColor)
            {
                cell.css("border-left", "2px solid " + borderColor);
            }
            if (columnNumber === this.columns.length - 1 || this.columns[columnNumber + 1].BorderColor !== borderColor)
            {
                cell.css("border-right", "2px solid " + borderColor);
            }
        }
        if (this.borderRows.includes(rowNumber))
        {
            cell.css("border-bottom", "1px solid black");
        }
        const row = this.dataManager.getData().getRow(rowNumber);
        if (row.HideBottomBorder)
        {
            cell.css("border-bottom", "0");
        }
        const cellData = this.dataManager.getData().getCell(rowNumber, columnNumber);
        if (value === cellData.EditValue)
        {
            let html = cellData.DisplayValue;
            if (!cellData.IsEditable)
            {
                html = "<i>" + html + "</i>";
            }
            if (cellData.Error)
            {
                cell.css("position", "relative");
                html +=
                    "<i style=\"color:red; top:0; right:0; position:absolute\" class=\"" + IconHelper.getIconClass(Icon.Error) + "\" /i>";
            }
            cell.html(html);
            cell.css("white-space", "nowrap");
            Helper.setColors(cell, cellData.Colors ?? [row.DefaultColor]);

            if (cellData.BorderColor)
            {
                const { topSVG, bottomSVG, leftSVG, rightSVG, triangleHeight, step } =
                    this.generateTriangleBorderSVG(cellData.BorderColor);

                cell.addClass("timesheet-cell-with-border");

                cell.get(0).style.setProperty(
                    "--triangle-bg",
                    `url("${topSVG}"), url("${bottomSVG}"), url("${leftSVG}"), url("${rightSVG}")`
                );

                cell.get(0).style.setProperty(
                    "--triangle-size",
                    `${step}px ${triangleHeight}px, ${step}px ${triangleHeight}px, ${triangleHeight}px ${step}px, ${triangleHeight}px ${step}px`
                );
            }
            else
            {
                cell.removeClass("timesheet-cell-with-border");
                cell.get(0).style.removeProperty("--triangle-bg");
                cell.get(0).style.removeProperty("--triangle-size");
            }
        }
        cell.tooltip({
            title: () =>
            {
                const cellInfo = this.dataManager.getData().getCell(rowNumber, columnNumber);
                return cellInfo.Error ?? cellInfo.Tooltip;
            },
            container: "body",
            placement: "top"
        });
    }

    private generateTriangleBorderSVG(color)
    {
        const triangleHeight = 4;
        const triangleBase = 8;
        const spacing = 7;
        const step = triangleBase + spacing;

        const topSVG = `data:image/svg+xml,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="${step}" height="${triangleHeight}">
            <polygon points="0,0 ${triangleBase},0 ${triangleBase / 2},${triangleHeight}" fill="${color}"/>
        </svg>
    `)}`;

        const bottomSVG = `data:image/svg+xml,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="${step}" height="${triangleHeight}">
            <polygon points="${triangleBase / 2},0 0,${triangleHeight} ${triangleBase},${triangleHeight}" fill="${color}"/>
        </svg>
    `)}`;

        const leftSVG = `data:image/svg+xml,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="${triangleHeight}" height="${step}">
            <polygon points="0,0 ${triangleHeight},${triangleBase / 2} 0,${triangleBase}" fill="${color}"/>
        </svg>
    `)}`;

        const rightSVG = `data:image/svg+xml,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="${triangleHeight}" height="${step}">
            <polygon points="0,${triangleBase / 2} ${triangleHeight},0 ${triangleHeight},${triangleBase}" fill="${color}"/>
        </svg>
    `)}`;

        return { topSVG, bottomSVG, leftSVG, rightSVG, triangleHeight, triangleBase, step };
    }
}
